/**
 * Lightweight image picker with cropping.
 * Replaces the old ImageSection class component for the "createNew" use case
 * used by NewItemModal and CreateWorkspaceModal.
 */
import { useState, useRef, useCallback, useEffect } from 'react';
import ReactCrop, { type Crop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { Upload } from 'lucide-react';
import { Spinner } from '@/components/ui/Spinner';

interface ImagePickerProps {
  useCase: string;
  pictureUploadingProcess: { status: string; data?: any };
  currentUserInfo?: any;
  updateCroppedImage: (blob: Blob) => void;
  croppedImageUrl?: string;
  handlePictureChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  item?: any;
}

const MAX_PX = 280;

function cropToBlob(image: HTMLImageElement, crop: Crop): Promise<Blob> {
  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;
  const canvas = document.createElement('canvas');
  canvas.width = crop.width * scaleX;
  canvas.height = crop.height * scaleY;
  const ctx = canvas.getContext('2d')!;
  ctx.drawImage(
    image,
    crop.x * scaleX, crop.y * scaleY,
    crop.width * scaleX, crop.height * scaleY,
    0, 0, canvas.width, canvas.height,
  );
  return new Promise((resolve) => canvas.toBlob((b) => resolve(b!), 'image/jpeg', 0.92));
}

export default function ImagePicker({
  pictureUploadingProcess,
  updateCroppedImage,
  handlePictureChange,
}: ImagePickerProps) {
  const { status, data } = pictureUploadingProcess;
  const imageRef = useRef<HTMLImageElement | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [crop, setCrop] = useState<Crop>();
  const [loading, setLoading] = useState(false);
  const prevStatusRef = useRef(status);

  // Prepare image for cropper when status changes to pictureSelected
  useEffect(() => {
    if (status === 'pictureSelected' && prevStatusRef.current !== 'pictureSelected' && data?.picture) {
      setLoading(true);
      setPreviewUrl(null);
      const file = data.picture as File;
      const url = file instanceof File ? URL.createObjectURL(file) : file;
      setPreviewUrl(url);
      setCrop(undefined); // will be set properly on image load
      setLoading(false);
    }
    if (status === 'initial' && prevStatusRef.current !== 'initial') {
      setPreviewUrl(null);
    }
    prevStatusRef.current = status;
  }, [status, data]);

  // Set a centered square crop when the image loads
  const handleImageLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    imageRef.current = e.currentTarget;
    const { width, height } = e.currentTarget;
    const side = Math.min(width, height) * 0.8;
    const initialCrop: Crop = {
      unit: 'px',
      width: side,
      height: side,
      x: (width - side) / 2,
      y: (height - side) / 2,
    };
    setCrop(initialCrop);
    // Trigger initial crop blob
    cropToBlob(e.currentTarget, initialCrop).then(updateCroppedImage);
  }, [updateCroppedImage]);

  const handleCropComplete = useCallback(async (c: Crop) => {
    if (!imageRef.current || !c.width || !c.height) return;
    const blob = await cropToBlob(imageRef.current, c);
    updateCroppedImage(blob);
  }, [updateCroppedImage]);

  // ── Initial state: show dropzone ──
  if (status === 'initial') {
    return (
      <label className="flex cursor-pointer flex-col items-center gap-2 rounded-lg border-2 border-dashed border-border px-6 py-4 text-muted-foreground transition hover:border-primary hover:text-primary">
        <Upload className="h-5 w-5" />
        <span className="text-sm font-medium">Add cover image (optional)</span>
        <input
          type="file"
          className="hidden"
          accept="image/png,image/jpeg,image/jpg,application/pdf"
          onChange={handlePictureChange}
        />
      </label>
    );
  }

  // ── PDF selected: show static preview ──
  if (status === 'pdfSelected') {
    if (data?.picture) {
      return (
        <div className="flex flex-col items-center gap-2">
          <img
            src={URL.createObjectURL(data.picture)}
            alt="PDF preview"
            className="max-h-40 rounded"
          />
          <label className="cursor-pointer text-sm font-medium text-primary hover:underline">
            Choose another
            <input
              type="file"
              className="hidden"
              accept="image/png,image/jpeg,image/jpg,application/pdf"
              onChange={handlePictureChange}
            />
          </label>
        </div>
      );
    }
    return <Spinner />;
  }

  // ── Image selected/cropped: show cropper ──
  if (status === 'pictureSelected' || status === 'pictureCropped') {
    if (loading || !previewUrl) return <Spinner />;
    return (
      <div className="flex flex-col items-center gap-2">
        <ReactCrop
          crop={crop}
          onChange={setCrop}
          onComplete={handleCropComplete}
          aspect={1}
          keepSelection
        >
          <img
            ref={imageRef}
            src={previewUrl}
            alt="crop preview"
            style={{ maxWidth: '100%', maxHeight: '50vh' }}
            onLoad={handleImageLoad}
          />
        </ReactCrop>
        <label className="cursor-pointer text-sm font-medium text-primary hover:underline">
          Choose another
          <input
            type="file"
            className="hidden"
            accept="image/png,image/jpeg,image/jpg,application/pdf"
            onChange={handlePictureChange}
          />
        </label>
      </div>
    );
  }

  return null;
}
